import { Box, Button, Typography } from "@mui/material";
import { CalendarClock, TimerReset } from "lucide-react";
import { useNavigate } from "react-router-dom";
import FlexBox from "components/common/FlexBox";
import useIsMobile from "components/hooks/useIsMobile";

export type ScheduledActionItem = {
  title: string;
  subtitle: string;
  to?: string;
  nextRunSec?: number;
};

export interface ScheduledActionsProps {
  actions?: ScheduledActionItem[];
  hideHeader?: boolean;
}

const MAX_VISIBLE = 3;

const ScheduledActions = ({
  actions = [],
  hideHeader = false,
}: ScheduledActionsProps) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const sortedActions = [...actions].sort(
    (a, b) => (a.nextRunSec ?? 0) - (b.nextRunSec ?? 0),
  );
  const visibleActions = sortedActions.slice(0, MAX_VISIBLE);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {!hideHeader && (
        <FlexBox direction="row" justifyContent="space-between">
          <FlexBox direction="row" gap="8px" alignItems="center">
            <CalendarClock size={20} color="#198282" />
            <Typography variant={isMobile ? "h3" : "h4"}>Routines</Typography>
          </FlexBox>
          <Button
            variant="text"
            sx={{ height: "auto" }}
            onClick={() => navigate("/scheduled-reminders")}
          >
            See all
          </Button>
        </FlexBox>
      )}
      <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {actions.length === 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              padding: "16px",
              borderRadius: "12px",
              border: "2px dashed #D4D4D4",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <TimerReset size={20} color="#9E9E9E" />
              <Typography variant="body" fontWeight={600}>
                No scheduled reminders yet
              </Typography>
            </Box>
            <Typography
              variant="bodySmall"
              color="text.secondary"
              noWrap
              sx={{ cursor: "pointer" }}
              onClick={() =>
                navigate("/conversations/in-app", {
                  state: {
                    prefill: "Remind me every Friday to review my week",
                  },
                })
              }
            >
              <Box
                component="span"
                sx={{ color: "primary.main", fontWeight: 700 }}
              >
                Try
              </Box>{" "}
              &ldquo;Remind me every Friday to review my week&rdquo;
            </Typography>
          </Box>
        )}
        {visibleActions.map((action, index) => (
          <Box
            key={`${action.title}-${index}`}
            title={[action.title, action.subtitle].filter(Boolean).join(" — ")}
            onClick={() => navigate(action.to ?? "/activity")}
            sx={(theme) => ({
              display: "flex",
              flexDirection: "column",
              alignItems: "start",
              gap: "2px",
              padding: "8px 12px",
              borderRadius: "10px",
              border: `1px solid ${theme.palette.gray[4]}`,
              cursor: "pointer",
              overflow: "hidden",
            })}
          >
            <Typography variant="body" noWrap sx={{ width: "100%" }}>
              {action.title}
            </Typography>
            {action.subtitle && (
              <Typography
                variant="bodySmall"
                color="text.secondary"
                noWrap
                sx={{ width: "100%" }}
              >
                {action.subtitle}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default ScheduledActions;
